import { memo } from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import HomeWorkOutlined from "@mui/icons-material/HomeWorkOutlined";
import HandymanOutlined from "@mui/icons-material/HandymanOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import MessageUsPanel from "../../Components/MessageForm/MessageUsPanel";

/**
 * "Work with us" (/property-professional). Was a placeholder heading. Agents,
 * brokers, contractors and partners who want to work with SimplerHome land
 * here from the footer; the form goes straight to Asher (Inquiry topic
 * "work-with-us").
 */
const VENDOR_PORTAL = process.env.REACT_APP_VENDOR_PORTAL_URL || "https://vendor.simplerhome.us";

const AUDIENCES = [
  {
    icon: HomeWorkOutlined,
    title: "Agents & brokers",
    body: "Give your clients a home record that follows the house: permits, repairs and improvements in one place, ready for the next sale.",
  },
  {
    icon: HandymanOutlined,
    title: "Contractors & home pros",
    body: "Get introduced to homeowners who need work done, and add finished jobs to their home's history.",
    action: { label: "Apply as a pro", href: `${VENDOR_PORTAL}/register` },
  },
  {
    icon: ApartmentOutlined,
    title: "Escrow, insurance & lenders",
    body: "Offer homeowners SimplerHome as a closing gift or a benefit, and help them keep their paperwork straight.",
  },
];

const PropertyProfessional = memo(() => (
  <Container maxWidth="xl" disableGutters>
    <FullPaddingWrapper>
      <Box
        sx={{
          background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
          borderRadius: { xs: "16px", md: "24px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          color: "#fff",
          p: { xs: 4, md: 8 },
          mb: { xs: 4, md: 6 },
        }}
      >
        <HandshakeOutlined sx={{ fontSize: { xs: 44, md: 56 }, mb: 2, opacity: 0.9 }} />
        <Typography variant="h2" sx={{ fontWeight: 700, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, mb: 2 }}>
          Work with SimplerHome
        </Typography>
        <Typography sx={{ fontSize: { xs: "1rem", md: "1.2rem" }, fontWeight: 300, maxWidth: 640, opacity: 0.9 }}>
          We partner with the people homeowners already trust. Tell us a little about what you do and we'll be in touch.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: { xs: 2, md: 3 },
          mb: { xs: 5, md: 8 },
        }}
      >
        {AUDIENCES.map(({ icon: Icon, title, body, action }) => (
          <Stack key={title} spacing={1.5} sx={{ p: { xs: 3, md: 4 }, borderRadius: "16px", backgroundColor: "#f8f8f8" }}>
            <Icon sx={{ color: "primary.main", fontSize: 32 }} />
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {title}
            </Typography>
            <Typography variant="body1" color="#333" sx={{ lineHeight: 1.6, flex: 1 }}>
              {body}
            </Typography>
            {action && (
              <Button href={action.href} variant="outlined" sx={{ alignSelf: "flex-start", textTransform: "none" }}>
                {action.label}
              </Button>
            )}
          </Stack>
        ))}
      </Box>

      <Box id="work-with-us-form" sx={{ pb: { xs: 2, md: 4 } }}>
        <MessageUsPanel
          align="center"
          topic="work-with-us"
          title="Tell us about you"
          intro="Who you are, what you do, and how you'd like to work together. A real person on our team reads every one."
          showPhone
          messageLabel="About you"
          messagePlaceholder="e.g. I'm a broker in Studio City with about 40 closings a year, and I'd like to offer SimplerHome to my buyers."
          submitLabel="Send"
        />
      </Box>
    </FullPaddingWrapper>
  </Container>
));

export default PropertyProfessional;
